import React, { useState, useEffect } from "react";
import { Row, Col, Card, Container, CardBody, Table } from "reactstrap";
import { Link } from "react-router-dom";
import axios from "../api/axios";
import logoSvg from "../../assets/images/rdig_logo.jpg";
import "../../assets/css/rentdigicare.css";
import { toast } from "react-toastify";
import FeatherIcon from "feather-icons-react";

const VendorPreview = () => {
  const [vendor, setVendor] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    verified: 0,
    rejected: 0,
    suspended: 0,
  });
  const [loading, setLoading] = useState(false);
  const [vendorError, setVendorError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const getDecodedUser = () => {
    try {
      const token = window.localStorage.getItem("accessToken");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const getVendorApiUrl = () => {
    const decode = getDecodedUser();
    const params = new URLSearchParams({
      page: currentPage,
      limit: 10,
    });

    if (decode?.role === "manager" && decode.company_id) {
      params.append("companyId", decode.company_id);
    }

    if (decode?.role === "company" && decode.id) {
      params.append("companyId", decode.id);
    }

    return `/vendor/get_vendors_preview?${params.toString()}`;
  };

  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      setVendorError("");

      try {
        const apiUrl = getVendorApiUrl();
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("accessToken")}`,
          },
        });

        console.log(response);

        if (response.data?.success && response?.data?.vendors?.length > 0) {
          setVendor(response?.data?.vendors);
          setStats(response?.data?.stats);
        } else {
          setVendor([]);
          setStats({
            total: 0,
            pending: 0,
            verified: 0,
            rejected: 0,
            suspended: 0,
          });
          setVendorError(response.data?.errorMessage);
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
        setVendorError("Failed to load vendor data");
        toast.error("Failed to load vendor data");
        setVendor([]);
        setStats({
          total: 0,
          pending: 0,
          verified: 0,
          rejected: 0,
          suspended: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, [currentPage]);

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(stats.total / 10);

  return (
    <React.Fragment>
      <div className="bg-soft-light min-vh-100 py-5">
        <div className="py-4">
          <Container>
            <Row>
              <Col lg={12}>
                <Card className="shadow-sm border-0">
                  <CardBody>
                    <Row className="justify-content-center mt-3 align-items-center">
                      <Col xs={8} className="text-center">
                        <div className="logo-top">
                          <img src={logoSvg} alt="Logo" height="70" className="mb-3" />
                        </div>
                      </Col>
                      <Col xs={2}></Col>
                    </Row>

                    <Row className="justify-content-center mt-3">
                      <Col lg={12}>
                        <Row>
                          {[
                            { label: 'Total', value: stats.total, color: 'primary' },
                            { label: 'Pending', value: stats.pending, color: 'warning' },
                            { label: 'Verified', value: stats.verified, color: 'success' },
                            { label: 'Rejected', value: stats.rejected, color: 'danger' },
                            { label: 'Suspended', value: stats.suspended, color: 'secondary' },
                          ].map(({ label, value, color }, index) => (
                            <Col md={2} className="mb-2" key={index}>
                              <Card className={`border-0 shadow-sm bg-${color} text-light`}>
                                <CardBody className="text-center py-2 px-2">
                                  <small style={{ fontSize: '0.8rem', opacity: 0.85 }}>{`${value} ${label}`}</small>
                                </CardBody>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </Col>
                    </Row>

                    <Row className="justify-content-center mt-3">
                      <Col lg={12}>
                        {loading ? (
                          <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <span className="ms-2 text-muted">Loading vendors...</span>
                          </div>
                        ) : vendorError ? (
                          <div className="alert alert-danger text-center" role="alert">
                            {vendorError}
                          </div>
                        ) : vendor.length === 0 ? (
                          <p className="text-center text-muted">No vendors available.</p>
                        ) : (
                          <>
                            <div className="table-responsive">
                              <Table className="table table-striped table-hover align-middle mb-0">
                                <thead className="table-primary">
                                  <tr>
                                    <th scope="col" style={{ width: '5%' }}>#</th>
                                    <th scope="col" style={{ width: '15%' }}>
                                      <FeatherIcon icon="user" size={16} className="me-2" />Name
                                    </th>
                                    <th scope="col" style={{ width: '15%' }}>
                                      <FeatherIcon icon="mail" size={16} className="me-2" />Email
                                    </th>
                                    <th scope="col" style={{ width: '12%' }}>
                                      <FeatherIcon icon="phone" size={16} className="me-2" />Contact
                                    </th>
                                    <th scope="col" style={{ width: '15%' }}>
                                      <FeatherIcon icon="building" size={16} className="me-2" />Agency
                                    </th>
                                    <th scope="col" style={{ width: '20%' }}>
                                      <FeatherIcon icon="list" size={16} className="me-2" />Specialties
                                    </th>
                                    <th scope="col" style={{ width: '15%' }}>
                                      <FeatherIcon icon="map-pin" size={16} className="me-2" />Address
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {vendor.map((vendorItem, index) => (
                                    <tr key={vendorItem._id}>
                                      <td>{(currentPage - 1) * 10 + index + 1}</td>
                                      <td>
                                        <strong className="text-dark">
                                          {vendorItem.first_name} {vendorItem.last_name}
                                        </strong>
                                      </td>
                                      <td>{vendorItem.email || "N/A"}</td>
                                      <td>{vendorItem.contact_no || "N/A"}</td>
                                      <td>{vendorItem.agency_name || "N/A"}</td>
                                      <td>
                                        {vendorItem.specialties?.map((s) => s.specialty).join(", ") || "None"}
                                      </td>
                                      <td>{vendorItem.address || "N/A"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                              <div className="d-flex justify-content-center mt-4">
                                <nav>
                                  <ul className="pagination">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                      <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                      >
                                        Previous
                                      </button>
                                    </li>
                                    {[...Array(totalPages)].map((_, index) => (
                                      <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                                        <button
                                          className="page-link"
                                          onClick={() => handlePageChange(index + 1)}
                                        >
                                          {index + 1}
                                        </button>
                                      </li>
                                    ))}
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                      <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                      >
                                        Next
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              </div>
                            )}
                          </>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </React.Fragment>
  );
};

export default VendorPreview;